/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import useSWR, { Middleware } from 'swr';
import useSWRInfinite from 'swr/infinite';
import useSWRMutation from 'swr/mutation';

import { OrganisationData, UserData } from '@/commons/commons';

export const getBFFURL = function () {
    return import.meta.env.VITE_API_URL;
};

export const authMiddleware: Middleware = function (useSWRNext) {
    const { getToken } = useAuth();

    return (key, fetcher, config) => {
        if (!fetcher) {
            return useSWRNext(key, fetcher, config);
        }

        const newFetcher = async (k: string, options?: any) => {
            const token = await getToken();
            options = options || {};
            options.auth = {
                authorization: `Bearer ${token}`,
            };
            const url = new URL(getBFFURL() + k);
            return fetcher(url.toString(), options);
        };

        return useSWRNext(key, newFetcher, config);
    };
};

export const getFetcher = async (url: string, options: any) => {
    const response = await fetch(url, {
        headers: options.auth,
    });
    if (response.status >= 400) {
        // useSWR will throw and only process a Error type
        // which means it will skip status and info like the
        // error we throw in mutate
        const res = await response.json();
        const error: ErrorWithInfo = new Error(
            `getFetcher got error ${response.status} with code ${res?.code} from GET ${url}`,
        );
        error.info = res;
        error.status = response.status;
        throw error;
    }
    return response.json();
};

export interface ErrorWithInfo extends Error {
    info?: any;
    status?: number;
}

export const mutateFetcher = async (key: string, options: Readonly<{ arg: any; auth: object; method: string }>) => {
    let url = key;
    if (options.arg?.idParam) {
        url = url.replace(/:id/, options.arg.idParam);
        delete options.arg.idParam;
    }
    if (options.arg?.idParam2) {
        url = url.replace(/:id2/, options.arg.idParam2);
        delete options.arg.idParam;
    }
    const headers: any = {
        ...options.auth,
    };
    const body = options.arg && Object.keys(options.arg).length && JSON.stringify(options.arg);
    if (body) {
        headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(url, {
        method: options.method,
        headers,
        body,
    });
    if (response.status >= 400) {
        const res = await response.json();
        const error: ErrorWithInfo = new Error(
            `mutateFetcher got error ${response.status} with code ${res?.code} from ${options.method} ${url}`,
        );
        error.info = res;
        error.status = response.status;
        throw error;
    }
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return response.json();
    }
    return null;
};

export const postFetcher = async (key: string, options: Readonly<{ arg: any; auth: object }>) => {
    return await mutateFetcher(key, { ...options, method: 'POST' });
};

export const patchFetcher = async (key: string, options: Readonly<{ arg: any; auth: object }>) => {
    return await mutateFetcher(key, { ...options, method: 'PATCH' });
};

export const putFetcher = async (key: string, options: Readonly<{ arg: any; auth: object }>) => {
    return await mutateFetcher(key, { ...options, method: 'PUT' });
};

export const deleteFetcher = async (key: string, options: Readonly<{ arg: any; auth: object }>) => {
    return await mutateFetcher(key, { ...options, method: 'DELETE' });
};

export const useGet = <T,>(url: string | (() => string) | ((s: string) => string) | null, options?: Object) => {
    return useSWR<T>(url, getFetcher, { use: [authMiddleware], ...options });
};

export const useGetInfinite = <T,>(
    getKey: (pageIndex: number, previousPageData: T | null) => string | null,
    options?: Object,
) => {
    return useSWRInfinite<T>(getKey, getFetcher, { use: [authMiddleware], ...options });
};

export const useMutate = <T = void,>(url: string | (() => string), options: any = {}) => {
    let fetcher = postFetcher;
    if (options.method === 'PATCH') {
        fetcher = patchFetcher;
    }
    if (options.method === 'DELETE') {
        fetcher = deleteFetcher;
    }
    if (options.method === 'PUT') {
        fetcher = putFetcher;
    }
    // @ts-ignore
    return useSWRMutation<T, any, any, any>(url, fetcher, {
        use: [authMiddleware],
        ...options,
    });
};

export const useCheckOrg = function () {
    const navigate = useNavigate();
    const { data, isLoading, error } = useGet<OrganisationData>('/bff/organisation');

    useEffect(() => {
        if (error?.info?.code === 'FORBIDDEN') {
            navigate('/forbidden');
        }

        if (error?.info?.code === 'EMPTY_ORGANISATION') {
            navigate('/org-setup');
        }
    }, [error]);

    return {
        error,
        organisation: data || null,
        isLoading,
    };
};

export const useUser = function () {
    const { data, isLoading, error } = useGet<UserData>('/bff/users/me');

    return {
        error,
        user: data || null,
        isLoading,
    };
};
